import type {
  CloseReasonCode,
  CloseReasonOption,
} from "@care-by-ella/contracts";

// TES-1208 — the structured reason behind "Avsluta utan svar",
// "Avsluta utan åtgärd" and "Avsluta utan kontakt". A-01 names the choices
// per kind of case (closeReasonsFor in contracts); the coach picks one, and
// may add a short note. "Annat" requires the note: A-01's "annat med
// obligatorisk fritext".

export type CloseReasonValue = { code: CloseReasonCode | null; note: string };

export const EMPTY_CLOSE_REASON: CloseReasonValue = { code: null, note: "" };

/** A reason is chosen, and "Annat" carries its note. */
export function closeReasonReady(v: CloseReasonValue): boolean {
  return v.code !== null && (v.code !== "other" || v.note.trim().length > 0);
}

/** The request body fields: the code, and the note when there is one. */
export function closeReasonBody(v: CloseReasonValue): {
  reasonCode: CloseReasonCode;
  reason?: string;
} {
  const note = v.note.trim();
  return { reasonCode: v.code!, ...(note ? { reason: note } : {}) };
}

export function CloseReasonPicker({
  name,
  legend,
  options,
  value,
  onChange,
  disabled = false,
}: {
  // Unique per rendered picker, so two open cards do not share one radio group.
  name: string;
  // Left out where a heading above already asks the question.
  legend?: string;
  options: readonly CloseReasonOption[];
  value: CloseReasonValue;
  onChange: (next: CloseReasonValue) => void;
  disabled?: boolean;
}) {
  const noteRequired = value.code === "other";
  return (
    <fieldset className="flex flex-col gap-xs" disabled={disabled}>
      <legend
        className={legend ? "mb-xs text-sm text-textPrimary" : "sr-only"}
      >
        {legend ?? "Orsak"}
      </legend>
      {options.map((o) => (
        <label key={o.code} className="flex items-center gap-sm text-sm">
          <input
            type="radio"
            name={name}
            value={o.code}
            checked={value.code === o.code}
            onChange={() => onChange({ ...value, code: o.code })}
          />
          {o.label}
        </label>
      ))}
      <textarea
        value={value.note}
        onChange={(e) => onChange({ ...value, note: e.target.value })}
        rows={2}
        maxLength={2000}
        className="mt-xs w-full rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary placeholder:text-textDisabled focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        aria-label={noteRequired ? "Beskriv orsaken" : "Kommentar (valfritt)"}
        aria-required={noteRequired}
        placeholder={
          noteRequired
            ? "Beskriv orsaken. Obligatoriskt."
            : "Kommentar (valfritt)"
        }
      />
    </fieldset>
  );
}
